import { FirebaseError } from "firebase/app";
import { AuthErrorCodes } from "firebase/auth";
import { Link, useLocation, useNavigate } from "react-router";
import { LinkButton } from "../../components/Button";
import Divider from "../../components/Divider";
import Form from "../../components/Form";
import FormField from "../../components/FormField";
import East from "../../components/icons/east.svg?react";
import Input from "../../components/Input";
import SocialIdentityProviders from "../../components/SocialIdentityProviders";
import { useAuth } from "../../context/authContext";
import styles from "./Signup.module.css";

interface SignupBody {
  email: string;
  password: string;
}

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const state = location.state as { from: Location } | undefined;
  const from = state?.from.pathname ?? "/";

  // TODO: should we add a plan selection as a first step?

  return (
    <div>
      <header className={styles.header}>
        <Link to="#" className={styles.logo}>
          only clicks
        </Link>
      </header>
      <div className={styles.container}>
        <h1 className={styles.title}>Create your account</h1>
        <Form<SignupBody>
          submitText="Sign up"
          onSubmit={({ value, formApi }) =>
            auth
              .signUp(value.email, value.password)
              .then(() => navigate(from, { replace: true }))
              .catch((err: FirebaseError) => {
                switch (err.code) {
                  case AuthErrorCodes.INVALID_EMAIL:
                    formApi.setFieldMeta("email", (e) => {
                      return {
                        ...e,
                        errorMap: {
                          ...e.errorMap,
                          onSubmit: "Invalid email address.",
                        },
                      };
                    });
                    break;
                  case AuthErrorCodes.EMAIL_EXISTS:
                    formApi.setFieldMeta("email", (e) => {
                      return {
                        ...e,
                        errorMap: {
                          ...e.errorMap,
                          onSubmit: "That email address is taken. Try another.",
                        },
                      };
                    });
                    break;
                  default:
                    formApi.setErrorMap({
                      onServer:
                        "Unexpected error occurred. Please try again." as never,
                    });
                    console.error(err);
                }
              })
          }
        >
          {(form) => (
            <div className={styles.formFields}>
              <form.Field name="email">
                {(field) => (
                  <FormField label="Email address" field={field}>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="email"
                      required={true}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </FormField>
                )}
              </form.Field>
              <form.Field
                name="password"
                validators={{
                  onSubmit: ({ value }) =>
                    value.length < 6
                      ? `Password should be at least 6 characters long, but got ${value.length}.`
                      : undefined,
                }}
              >
                {(field) => (
                  <FormField label="Password" field={field}>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="password"
                      required={true}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </FormField>
                )}
              </form.Field>
            </div>
          )}
        </Form>
        <Divider text="or" />
        <SocialIdentityProviders
          onSuccess={() => {
            navigate(from, { replace: true });
          }}
        />
        <p className={styles.login}>
          Already have an account?
          <LinkButton linkProps={{ to: "/login" }} glyph={East}>
            Login
          </LinkButton>
        </p>
      </div>
    </div>
  );
};

export default Signup;
